import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export function WizardLayout({
  steps,
  currentStep,
  onNext,
  onBack,
  fade,
  userName,
  isLastStep,
  onSubmit,
  submitDisabled,
  nextDisabled,
  nextLabel,
  submitLabel,
  children,
}) {
  const [shaking, setShaking] = useState(false);
  const navigate = useNavigate();

  const handleNextClick = () => {
    const isDisabled = isLastStep ? submitDisabled : nextDisabled;
    if (isDisabled) {
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
      // Still call the handler so the form can show validation errors
      if (isLastStep) {
        onSubmit?.();
      } else {
        onNext?.();
      }
      return;
    }
    if (isLastStep) {
      onSubmit?.();
    } else {
      onNext?.();
    }
  };

  const isDisabled = isLastStep ? submitDisabled : nextDisabled;

  return (
    <div className="min-h-screen font-sans flex justify-center px-3 py-4 sm:px-4" style={{ background: "#FCFAEE" }}>
      <div className="w-full max-w-[420px]">
        {/* Back to dashboard */}
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="wizard-home-btn"
          className="flex items-center gap-1 mb-3 px-1 transition-opacity duration-200 hover:opacity-70 active:scale-[0.97]"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px 0",
            color: "#00628F",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <ChevronLeft size={16} />
          Mi panel
        </button>

        {/* Header */}
        <div className="flex justify-between items-center mb-4 px-1">
          <div>
            <div
              className="font-bold tracking-[1.5px] uppercase"
              style={{ fontSize: 10, color: "#71644C" }}
            >
              WE 360
            </div>
            <div
              className="font-bold mt-0.5"
              style={{ fontSize: 14, color: "#2D2A24" }}
              data-testid="wizard-header-greeting"
            >
              {userName ? `Hola, ${userName}` : "Registro de salud"}
            </div>
          </div>
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold"
            style={{
              background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
              boxShadow: "0 4px 12px rgba(0,98,143,0.15)",
            }}
            data-testid="wizard-avatar"
          >
            {userName ? userName[0].toUpperCase() : "W"}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-5 px-1">
          <div className="flex justify-between mb-1.5" style={{ fontSize: 11 }}>
            <span className="font-semibold" style={{ color: "#2D2A24" }}>
              {steps[currentStep]?.title}
            </span>
            <span className="font-bold" style={{ color: "#00628F" }} data-testid="wizard-step-counter">
              {currentStep + 1} / {steps.length}
            </span>
          </div>
          <div className="flex gap-[3px]" data-testid="wizard-progress-bar">
            {steps.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-1 rounded-full"
                style={{
                  background:
                    i < currentStep
                      ? "#00628F"
                      : i === currentStep
                        ? "rgba(0,98,143,0.56)"
                        : "#E2D9CB",
                  transition: "background 0.4s ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Content card */}
        <div
          className="we-card p-6"
          style={{
            opacity: fade ? 1 : 0,
            transform: fade ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
          }}
          data-testid="wizard-content-card"
        >
          <div className="text-center mb-4">
            <div className="text-lg font-bold" style={{ color: "#2D2A24" }}>
              {steps[currentStep]?.title}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "#71644C" }}>
              {steps[currentStep]?.sub}
            </div>
          </div>
          {children}
        </div>

        {/* Navigation */}
        <div className="flex gap-2.5 mt-4 px-1">
          {currentStep > 0 && (
            <button
              onClick={onBack}
              data-testid="wizard-back-btn"
              className="cursor-pointer text-sm font-semibold active:scale-[0.97] transition-transform"
              style={{
                flex: "0 0 auto",
                padding: "14px 20px",
                borderRadius: 14,
                border: "1.5px solid #E2D9CB",
                background: "#FFFFFF",
                color: "#71644C",
              }}
            >
              &larr; Atrás
            </button>
          )}
          <button
            onClick={handleNextClick}
            data-testid={isLastStep ? "wizard-submit-btn" : "wizard-next-btn"}
            className={`flex-1 cursor-pointer ${shaking ? "we-shake" : ""}`}
            style={{
              padding: 15,
              borderRadius: 14,
              border: "none",
              background: isDisabled
                ? "#B5A998"
                : "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
              color: "white",
              fontSize: 15,
              fontWeight: 700,
              boxShadow: isDisabled
                ? "none"
                : "0 6px 20px rgba(0,98,143,0.15)",
              opacity: isDisabled ? 0.5 : 1,
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {isLastStep ? (submitLabel || "Enviar registro") : (nextLabel || "Siguiente \u2192")}
          </button>
        </div>
      </div>
    </div>
  );
}
